import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { auth } from '../../../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface AttachmentContent {
  url: string;
  type: string;
  name: string;
}

const useAttachmentPreview = () => {
  const [previewAttachment, setPreviewAttachment] = useState<any>(null);
  const [attachmentContent, setAttachmentContent] = useState<AttachmentContent | null>(null);
  const [loadingAttachment, setLoadingAttachment] = useState(false);
  const [docxHtml, setDocxHtml] = useState<string | null>(null);
  
  // Build download URL with token - used for both preview and download
  const buildAttachmentUrl = useCallback((attachment: any, email: any): string | null => {
    const token = auth.getToken();
    const messageId = email?.outlook_message_id || email?.id;
    const attachmentId = attachment?.id || attachment?.outlook_attachment_id || attachment?.attachmentId;
    
    if (!attachmentId || !messageId) {
      console.error('Missing attachment or message ID', { attachmentId, messageId });
      return null;
    }
    
    return `${API}/outlook/emails/${messageId}/attachments/${attachmentId}/download?token=${encodeURIComponent(token || '')}`;
  }, []);

  // Download attachment using direct link
  const downloadAttachment = useCallback(async (attachment: any, email: any) => {
    if (!attachment || !email) return;
    
    try {
      const downloadUrl = buildAttachmentUrl(attachment, email);
      if (!downloadUrl) {
        toast.error('Vedlegg mangler ID - kan ikke lastes ned');
        return;
      }
      
      const fileName = attachment.name || attachment.filename || 'attachment';
      
      // Create hidden link and click it
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = fileName;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      
      setTimeout(() => {
        document.body.removeChild(a);
      }, 100);
      
      toast.success('Vedlegg lastes ned...');
    } catch (err) {
      console.error('Error downloading attachment:', err);
      toast.error('Kunne ikke laste ned vedlegg');
    }
  }, [buildAttachmentUrl]);

  // Preview attachment - NO JavaScript fetch, just build URL for browser to load
  const previewAttachmentHandler = useCallback(async (attachment: any, email: any) => {
    if (!attachment || !email) return;
    
    const fileName = attachment.name || attachment.filename || 'attachment';
    const contentType = attachment.contentType || attachment.content_type || 'application/octet-stream';
    
    // Build direct URL
    const previewUrl = buildAttachmentUrl(attachment, email);
    if (!previewUrl) {
      toast.error('Kunne ikke åpne vedlegg - mangler ID');
      return;
    }
    
    // Determine file type
    const isImage = contentType.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(fileName);
    const isPdf = contentType === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf');
    const isDocx = contentType.includes('wordprocessingml') || fileName.toLowerCase().endsWith('.docx');
    
    // Set preview state
    setPreviewAttachment(attachment);
    setDocxHtml(null);
    setLoadingAttachment(false);
    
    if (isImage || isPdf) {
      // For images and PDFs - browser can render directly via URL
      // No JavaScript fetch needed - browser loads the content natively
      setAttachmentContent({
        url: previewUrl,
        type: isPdf ? 'application/pdf' : contentType,
        name: fileName
      });
    } else if (isDocx) {
      // For DOCX files - we need to download for now
      // Show a message and trigger download
      setAttachmentContent({
        url: '',
        type: contentType,
        name: fileName
      });
      toast.info('Word-dokumenter må lastes ned for å åpnes');
    } else {
      // Unsupported format - show download option
      setAttachmentContent({
        url: '',
        type: contentType,
        name: fileName
      });
    }
  }, [buildAttachmentUrl]);
  
  // Close preview
  const closePreview = useCallback(() => {
    setPreviewAttachment(null);
    setAttachmentContent(null);
    setDocxHtml(null);
  }, []);
  
  return {
    previewAttachment,
    attachmentContent,
    docxHtml,
    loadingAttachment,
    previewAttachmentHandler,
    downloadAttachment,
    closePreview
  };
};

export default useAttachmentPreview;
